import "./components/AddDocumentTemplateStyle.css";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Editor } from "react-draft-wysiwyg";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EndPoints } from "../../../../../utils/apiEndpoints";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import { FieldErrors } from "../../../../../domain/helpers/Errors";
import { validateSchema } from "../../../../../common/ValidateSchema";
import { darkModeBackgroudInputs } from "../../../../../utils/constants";
import { useDarkModeStore } from "../../../../../store/useDarkModeStore";
import { useLanguageStore } from "../../../../../store/useLanguageStore";
import { customBlockRenderFunc, entityMapper } from "./components/DraftJsFunctions";
import { DocumentTemplateInterface } from "../../../../../models/ProcurementModule/DocumentTemplate";
import { documentTemplateSchema } from "../../../../../domain/ValidationSchemas/PurchaseOrder/documentTemplate";
import Button from "@mui/joy/Button";
import TableSelect from "./TableSelect";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import SchemaInsertButton from "./SchemaInsertButton";
import LoadingIcon from "../../../../../common/LoadingIcon";
import ToastMessage from "../../../../../common/ToastMessage";
import useAddDocumentTemplate from "../../../../../store/ReactQueries/general/documentTemplate/useAddDocumentTemplate";
import useDocumentTemplateById from "../../../../../store/ReactQueries/general/documentTemplate/useDocumentTemplateById";
import useUpdateDocumentTemplate from "../../../../../store/ReactQueries/general/documentTemplate/useUpdateDucumentTemplate";

export type ModeTypes = "Add" | 'Edit' | 'View';

interface AddDoctemplateProps {
  mode: ModeTypes
}
export const AddDocumentTemplate = ({ mode }: AddDoctemplateProps) => {
  const isAdd = mode === 'Add';
  const isView = mode === 'View';

  const navigate = useNavigate();

  const { language } = useLanguageStore();
  const { darkMode } = useDarkModeStore();
  const { id: tempId } = useParams<{ id: string; }>();
  const { data: docTempById, error } = useDocumentTemplateById(tempId ?? '');

  const addDocumentTemplate = useAddDocumentTemplate(1, 20);
  const editDocumentTemplate = useUpdateDocumentTemplate(1, 20);

  const [errors, setErrors] = useState<FieldErrors>({});
  const [disableSaveBtn, setDisableBtnSave] = useState<boolean>(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [selectSchema, setSelectSchema] = useState<'RFQ' | 'RFP' | 'PR' | 'PO' | ''>('');
  const [form, setForm] = useState<DocumentTemplateInterface>({
    id: '',
    title: '',
    content: '',
    documentType: undefined,
  });

  useEffect(() => {
    if (mode === 'Add') {
      setSelectSchema('');
      setEditorState(EditorState.createEmpty());
      delete errors?.['content'];
      setForm({
        ...form,
        id: '',
        title: '',
        content: '',
        documentType: undefined,
      });
    } else if (docTempById) {
      const result = (docTempById.title ?? '') as 'RFQ' | 'RFP' | 'PR' | 'PO' | '';
      setForm({
        ...form,
        id: docTempById.id,
        title: result,
        content: docTempById.content ?? '',
      })
      setSelectSchema(result);

      const contentBlock = htmlToDraft(docTempById.content ?? '');
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        const newEditorState = EditorState.createWithContent(contentState);
        setEditorState(newEditorState);
        delete errors?.['content'];
      }
    }
  }, [docTempById]);

  useEffect(() => {
    if (error) {
      ToastMessage.Failed(`Error: ${error.message}`);
      navigate(-1);
    }
  }, [error]);

  const onEditorStateChange = (editor: any) => {
    delete errors?.['content'];
    const editorHTML = draftToHtml(
      convertToRaw(editor.getCurrentContent()),
      undefined,
      false,
      entityMapper
    );
    setEditorState(editor);
    setForm({ ...form, content: editorHTML });
  };

  const uploadImageCallBack = async (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve({ data: { link: reader.result } });
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSave = async () => {
    const isValid = validateSchema(documentTemplateSchema, form);
    setErrors(isValid.errors);
    if (!isValid.result) return;

    setDisableBtnSave(true);

    try {
      if (tempId && tempId.length !== 0) {
        editDocumentTemplate.mutate(form, {
          onSuccess: () => {
            ToastMessage.Success();
          },
          onError: (e) => ToastMessage.Failed(e.message ?? undefined),
        })
      } else {
        let wrappedContent = `<div style="width: 794px; padding: 40px; margin: auto; background: white;">${form.content}</div>`;

        const result = {
          title: form.title,
          content: wrappedContent,
          documentType: form.documentType
        }

        addDocumentTemplate.mutate(
          result,
          {
            onSuccess: () => {
              ToastMessage.Success();
            },
            onError: (e) => ToastMessage.Failed(e.message ?? undefined),
          }
        );
      }
      setTimeout(() => {
        setDisableBtnSave(false);
      }, 500);
      navigate(EndPoints.DOCUMENTTEMPLATE)
    } catch (error) {
      console.error("Save error:", error);
      ToastMessage.Failed();
      setDisableBtnSave(false);
    }
  };
  return <div className="d-flex flex-column gap-2 p-2" style={{ borderRadius: 10, minHeight: 300 }}>
    <div className="editor-container">
      <div className="mt-2 mb-3 d-flex flex-row gap-4 align-items-center justify-content-between">
        <TableSelect
          form={form}
          errors={errors}
          disabledDropdown={!isAdd}
          selectSchema={selectSchema}
          setForm={setForm}
          setEditorState={setEditorState}
          setSelectSchema={setSelectSchema}
        />
      </div>
      <Editor
        editorState={editorState}
        toolbarClassName="toolbarClassName"
        wrapperClassName="wrapperClassName"
        editorClassName="editorClassName"
        onEditorStateChange={onEditorStateChange}
        customBlockRenderFunc={customBlockRenderFunc}
        toolbarCustomButtons={[
          <SchemaInsertButton
            key="schema"
            editorState={editorState}
            selectSchema={selectSchema}
            onEditorStateChange={onEditorStateChange}
          />
        ]}
        readOnly={isView}
        toolbarHidden={isView}
        toolbar={{
          image: {
            urlEnabled: true,
            previewImage: true,
            uploadEnabled: true,
            inputAccept: 'image/*',
            uploadCallback: uploadImageCallBack,
            alt: { present: true, mandatory: false },
          },
        }}
      />
      {
        (errors?.['content'])
        && <span style={{ fontSize: 12, color: 'red' }}>{errors?.['content']}</span>
      }
      <div className="d-flex flex-row align-items-center justify-content-end pe-5 gap-3">
        <Button
          className='font-open-sans'
          sx={{
            fontSize: "16px",
            border: `1px solid var(--btn-color)`,
            color: darkMode ? 'white' : "var(--btn-color)",
            background: darkMode ? darkModeBackgroudInputs : "transparent",
            "&:hover": {
              background: darkMode ? darkModeBackgroudInputs : "transparent",
              opacity: .7,
            },
          }}
          onClick={() => navigate(EndPoints.DOCUMENTTEMPLATE)}
        >
          {language['Cancel']}
        </Button>

        {
          !isView && <Button
            className='font-open-sans'
            sx={{
              color: "white",
              fontSize: "16px",
              background: "var(--btn-color)",
              width: 85,
              "&:hover": {
                opacity: .7,
                background: "var(--btn-color)",
              },
              "&.Mui-disabled": {
                color: "white",
                cursor: "not-allowed",
                pointerEvents: "none",
              },
            }}
            onClick={handleSave}
          >
            {
              disableSaveBtn
                ? <LoadingIcon width={25} />
                : language["Save"]
            }
          </Button>
        }
      </div>
    </div>
  </div>
}
export default AddDocumentTemplate;